import React from 'react';
import { Pressable, View } from 'react-native';
import { Check } from 'lucide-react-native';
import { Text } from '@/components/ui/text';
import { Icon } from '@/components/ui/icon';
import { useLocalizedValue } from '@/hooks/useLocalizedValue';
import { cn } from '@/lib/utils';
import type { WorkoutSetSequenceItem } from '~/workout/hooks/useWorkoutSetSequence';

interface SetListRowProps {
  /** The sequence item this row represents */
  item: WorkoutSetSequenceItem;
  /** Whether this row is the currently selected (active card) set */
  isSelected: boolean;
  /** Formatted values line, e.g. "80kg × 10" */
  meta: string;
  /** Toggle the set's completion (tapping the leading circle) */
  onToggleComplete: () => void;
  /** Select this set as the active one (tapping the row body) */
  onSelect: () => void;
}

/**
 * Compact, read-only set row in the in-progress overview list.
 *
 * - Leading circle: green check when completed, accent number when selected,
 *   outlined muted number otherwise. Tapping it toggles completion.
 * - Tapping the row body selects the set for the active card.
 */
export const SetListRow: React.FC<SetListRowProps> = ({
  item,
  isSelected,
  meta,
  onToggleComplete,
  onSelect,
}) => {
  const localizedName = useLocalizedValue(item.sportActivity.name);
  const isCompleted = Boolean(item.set.completedAt);

  return (
    <Pressable
      onPress={onSelect}
      accessibilityRole="button"
      className={cn(
        // Always reserve a 2px border so toggling selection doesn't shift layout.
        'flex-row items-center gap-3 rounded-xl border-2 border-transparent bg-surface p-3.5',
        isSelected && 'border-accent',
        // Only upcoming (not completed, not selected) sets are dimmed;
        // completed sets stay full opacity — the green check is the cue.
        !isCompleted && !isSelected && 'opacity-70',
      )}
    >
      <Pressable
        onPress={onToggleComplete}
        accessibilityRole="button"
        hitSlop={8}
        className={cn(
          'h-7 w-7 items-center justify-center rounded-full',
          isCompleted && 'bg-positive',
          !isCompleted && isSelected && 'bg-accent',
          !isCompleted && !isSelected && 'border-2 border-border',
        )}
      >
        {isCompleted ? (
          <Icon as={Check} size={14} className="text-white" />
        ) : (
          <Text
            className={cn(
              'text-xs font-bold',
              isSelected ? 'text-accent-text' : 'text-text-muted',
            )}
          >
            {item.setNumber}
          </Text>
        )}
      </Pressable>

      <View className="flex-1 gap-0.5">
        <Text
          className={cn(
            'text-sm font-medium',
            isSelected ? 'text-accent' : 'text-text-primary',
          )}
        >
          {localizedName}
        </Text>
        <Text className="text-xs text-text-secondary">{meta}</Text>
      </View>
    </Pressable>
  );
};
